import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatusPill } from "@/components/shared/StatusPill";
import { AICard, AIBadge } from "@/components/shared/AICard";
import { CommunicationTimeline } from "@/components/shared/CommunicationTimeline";
import { DocumentPanel } from "@/components/shared/DocumentPanel";
import { AutoFileDialog } from "@/components/shared/AutoFileDialog";
import { Loading } from "@/components/shared/Loading";
import { HandoffConfirmDialog } from "@/components/shared/HandoffConfirmDialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useCustomer, useCommunications, useCustomerDocuments, useCustomerDocumentChecklist, useMe, useRole, useFinanceNextAction } from "@/api/hooks";
import { api } from "@/api/client";
import { useQueryClient } from "@tanstack/react-query";
import { money, initials } from "@/lib/format";
import { useApp } from "@/context/AppContext";
import { apiErrorMessage } from "@/lib/apiError";
import { Mail, MessageCircle, Phone, ArrowLeft, ArrowRight, FileText, Boxes, ChevronRight, ChevronDown, Loader2, Sparkles, UploadCloud, Check, X, Paperclip, History, ShieldCheck, ShieldAlert, Pencil, Eye } from "lucide-react";
import { useFileSelectConfirm, FileSelectConfirmBar } from "@/components/shared/FileSelectConfirm";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { BusinessStage, BusinessStageProgress, stageForCommercial } from "@/components/shared/BusinessStage";
import Workspace from "./Workspace";
import { FinanceDocumentsSections, FinanceFinalInvoice, FinanceCostsVariance, FinancePaymentOutstanding } from "@/components/finance/FinanceProfileSections";

// Item 6/7 (Aug 2026 Sales dashboard scope pass) — completed/in-progress
// shipments look like Job Details to Sales, but the payload here is
// already the Sales-safe trade-summary projection (see
// backend server.py `strip_job_for_sales` — no milestones, parties,
// booking, customs, or vendor data). This is intentionally the ONLY place
// Sales sees anything job-shaped: a commercial-summary record, opened in a
// modal, never a navigation into Operations' Job Detail page.
const CLOSED_JOB_STATUSES = new Set([
  "DELIVERED", "POD_RECEIVED", "POD_VERIFIED", "OPERATIONALLY_CLOSED",
  "CLOSED", "FINANCIALLY_CLOSED", "ARCHIVED", "AUDITED",
]);

function TradeDetailDialog({ trade, open, onOpenChange }) {
  if (!trade) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="trade-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{trade.id} · {trade.mode || "—"}</DialogTitle>
          <DialogDescription className="sr-only">Commercial summary for this past trade.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-y-3 gap-x-2 text-sm">
          <Field label="Route" value={trade.lane || "—"} />
          <div><div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Status</div><StatusPill status={trade.status || "—"} className="mt-0.5" /></div>
          <Field label="Commodity" value={trade.commodity || "—"} />
          <Field label="Date" value={(trade.updatedAt || trade.createdAt || "").slice(0, 10) || "—"} />
          {!trade.costsRestricted && <Field label="Value" value={trade.estRevenue != null ? money(trade.estRevenue, trade.currency) : "—"} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Aug 2026 — Customer Requirements. Names deliberately match the exact
// fields commercial_svc.convert_lead_to_inquiry()/update_inquiry() already
// read/write (server.py), so an edit here needs no field translation either
// direction — same requirement record a Bolna call's AI extraction attaches
// to (see backend _attach_bolna_requirements), just editable by Sales too.
const REQUIREMENT_FIELD_DEFS = [
  { key: "mode", label: "Shipment Mode" },
  { key: "direction", label: "Direction" },
  { key: "origin", label: "Origin" },
  { key: "destination", label: "Destination" },
  { key: "commodity", label: "Commodity / Cargo" },
  { key: "weight", label: "Weight", suffix: " kg", numeric: true },
  { key: "cbm", label: "Volume / CBM", numeric: true },
  { key: "pieces", label: "Quantity", numeric: true },
  { key: "incoterm", label: "Incoterm" },
  { key: "cargoReadyDate", label: "Ready Date / Expected Shipment" },
  { key: "deliveryAppointmentRequired", label: "Delivery Requirement", bool: true, trueLabel: "Delivery appointment required" },
  { key: "insuranceRequired", label: "Insurance Requirement", bool: true, trueLabel: "Insurance required" },
  { key: "customerNotes", label: "Customer Notes", wide: true },
];

function RequirementValue({ def, value, aiExtracted, uncertain }) {
  const empty = value === null || value === undefined || value === "";
  let display = empty ? "Not Provided" : value;
  if (!empty && def.bool) display = value ? def.trueLabel : "Not Provided";
  else if (!empty && def.suffix) display = `${value}${def.suffix}`;
  return (
    <div className={def.wide ? "col-span-2" : ""}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
        {def.label}
        {!empty && uncertain && <span className="normal-case text-amber-600 font-semibold">· Needs Confirmation</span>}
        {!empty && aiExtracted && !uncertain && <AIBadge>AI Extracted</AIBadge>}
      </div>
      <div className={`text-sm font-medium ${empty ? "text-slate-400 italic" : "text-slate-800"}`}>{display}</div>
    </div>
  );
}

function RequirementsEditDialog({ open, onOpenChange, initial, onSave, busy }) {
  const [values, setValues] = useState({});
  useEffect(() => {
    if (open) setValues(Object.fromEntries(REQUIREMENT_FIELD_DEFS.map((d) => [d.key, initial?.[d.key] ?? (d.bool ? false : "")])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="requirements-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Edit Customer Requirements</DialogTitle>
          <DialogDescription>Correct, complete, or update what the customer has told us — Sales has the final say over AI-extracted values.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {REQUIREMENT_FIELD_DEFS.map((d) => (
            <div key={d.key} className={d.wide ? "col-span-2" : ""}>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{d.label}</Label>
              {d.bool ? (
                <div className="flex items-center gap-2 h-9">
                  <Checkbox checked={!!values[d.key]} onCheckedChange={(v) => setValues((s) => ({ ...s, [d.key]: !!v }))} data-testid={`req-field-${d.key}`} />
                  <span className="text-sm text-slate-600">{d.trueLabel}</span>
                </div>
              ) : d.wide ? (
                <Textarea rows={2} value={values[d.key] ?? ""} onChange={(e) => setValues((s) => ({ ...s, [d.key]: e.target.value }))} data-testid={`req-field-${d.key}`} />
              ) : (
                <Input type={d.numeric ? "number" : "text"} value={values[d.key] ?? ""} onChange={(e) => setValues((s) => ({ ...s, [d.key]: e.target.value }))} data-testid={`req-field-${d.key}`} />
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSave(values)} disabled={busy} data-testid="requirements-save">
            {busy && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}Save Requirements
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// `source` is either the active (not-yet-converted) Lead — requirements live
// in `lead.qualification` — or the active Inquiry, which already stores
// these exact fields directly. Whichever is active is the single place this
// reads from and writes to; there is never a second, disconnected copy.
function RequirementsSection({ source, sourceType, customerId, onSaved }) {
  const [editOpen, setEditOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [extracting, setExtracting] = useState(false);
  if (!source) return null;
  const values = sourceType === "lead" ? (source.qualification || {}) : source;
  const aiExtracted = new Set(source.aiExtractedFields || []);
  const uncertain = new Set(source.aiUncertainFields || []);
  // Bug fix: convert_lead_to_inquiry() always coerces deliveryAppointmentRequired/
  // insuranceRequired to a real boolean (bool(None) === False), never null — so
  // a bare "is it set" check on those two fields was always true immediately
  // after conversion, hiding the empty-state "Extract from Conversation (AI)"
  // action behind a grid that only ever showed "Not Provided" everywhere else.
  // A boolean field only counts as "provided" when it's actually true — that
  // already matches how RequirementValue itself renders `false` as "Not Provided".
  const hasAny = REQUIREMENT_FIELD_DEFS.some((d) => {
    const v = values[d.key];
    if (d.bool) return v === true;
    return v !== null && v !== undefined && v !== "";
  });

  const save = async (formValues) => {
    setBusy(true);
    try {
      const fields = {};
      for (const d of REQUIREMENT_FIELD_DEFS) {
        let v = formValues[d.key];
        if (d.numeric) v = v === "" ? null : Number(v);
        if (!d.bool && v === "") v = null;
        fields[d.key] = v;
      }
      if (sourceType === "lead") {
        await api.patch(`/leads/${source.id}/qualification`, { fields, expectedVersion: source.version });
      } else {
        const changedKeys = new Set(Object.keys(fields));
        const nextAiExtracted = (source.aiExtractedFields || []).filter((k) => !changedKeys.has(k));
        const nextAiUncertain = (source.aiUncertainFields || []).filter((k) => !changedKeys.has(k));
        await api.patch(`/commercial/inquiries/${source.id}`, { fields: { ...fields, aiExtractedFields: nextAiExtracted, aiUncertainFields: nextAiUncertain }, expectedVersion: source.version });
      }
      toast.success("Requirements updated");
      setEditOpen(false);
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Could not save requirements")); }
    finally { setBusy(false); }
  };

  // Auto-extraction on an inbound call is best-effort (see backend
  // _attach_bolna_requirements) — it silently no-ops if the AI call fails
  // (e.g. a provider rate limit), so the transcript/summary can be fully
  // saved with nothing extracted from it yet. This retries against that
  // same saved conversation history — no new extraction logic.
  const extract = async () => {
    setExtracting(true);
    try {
      await api.post(`/customers/${customerId}/extract-requirements`);
      toast.success("Requirements extracted from conversation history");
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Could not extract requirements from the conversation yet")); }
    finally { setExtracting(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="c360-requirements">
      <div className="flex items-center justify-between mb-2.5">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          Customer Requirements {sourceType === "lead" && <span className="normal-case text-slate-400">· from Lead {source.id}, not yet converted</span>}
        </div>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditOpen(true)} data-testid="c360-edit-requirements">
          <Pencil className="h-3 w-3 mr-1.5" /> Edit Requirements
        </Button>
      </div>
      {hasAny ? (
        <div className="grid grid-cols-2 gap-y-3 gap-x-4">
          {REQUIREMENT_FIELD_DEFS.map((d) => (
            <RequirementValue key={d.key} def={d} value={values[d.key]} aiExtracted={aiExtracted.has(d.key)} uncertain={uncertain.has(d.key)} />
          ))}
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm text-slate-400 italic">No requirements captured yet.</div>
          <button onClick={extract} disabled={extracting} data-testid="c360-extract-requirements"
            className="shrink-0 inline-flex items-center gap-1.5 text-sm font-medium text-violet-700 hover:underline disabled:opacity-60">
            {extracting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            {extracting ? "Extracting…" : "Extract from Conversation (AI)"}
          </button>
        </div>
      )}
      <RequirementsEditDialog open={editOpen} onOpenChange={setEditOpen} initial={values} onSave={save} busy={busy} />
    </div>
  );
}

function FinanceNextActionCard({ customerId }) {
  const { data: nextAction } = useFinanceNextAction(customerId);
  if (!nextAction) return null;
  const isDone = nextAction.actionType === "none";
  return (
    <div className={`rounded-xl border p-4 ${isDone ? "border-emerald-200 bg-emerald-50" : "border-indigo-200 bg-indigo-50"}`} data-testid="c360-finance-next-action">
      <div className={`flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider ${isDone ? "text-emerald-600" : "text-indigo-500"}`}>
        AI Next Action
        {nextAction.aiGenerated && <AIBadge>AI</AIBadge>}
      </div>
      <div className={`text-base font-display font-semibold mt-0.5 ${isDone ? "text-emerald-900" : "text-indigo-900"}`}>{nextAction.what}</div>
      {nextAction.why && <div className={`text-[13px] mt-1 ${isDone ? "text-emerald-700" : "text-indigo-700"}`}>{nextAction.why}</div>}
      {nextAction.aiGenerated === false && (
        <div className="text-[11px] mt-1.5 text-slate-400 italic">AI recommendation unavailable — showing the current required action</div>
      )}
    </div>
  );
}

// Aug 2026 onboarding-to-pipeline connective fix — the single source of
// truth for "where is this customer in the Sales process, and what's the
// next action". Computed entirely from data GET /customers/{cid} already
// returns (kycReviewStatus, leads, inquiries incl. completeness +
// acceptanceStatus added server-side for exactly this purpose) — no new
// heavy endpoint, no invented pipeline stages. Used by BOTH the top-of-
// profile next-action banner and the "Send to Finance" checklist replacement
// so the two never disagree with each other.
//
// A customer can have MULTIPLE trades over time (item 12): "active" here
// always means the single most-recently-created inquiry that is not yet in
// a terminal state (WON/LOST/EXPIRED/ON_HOLD_CUSTOMER) — closed trades stay
// exactly where they already lived, in "Previous Trades", untouched.
const TERMINAL_INQUIRY_STATUSES = new Set(["WON", "LOST", "EXPIRED", "ON_HOLD_CUSTOMER"]);
const OPEN_LEAD_STATUSES = new Set(["NEW", "CONTACTED", "QUALIFIED"]);

function computeWorkflowState(c) {
  const kycApproved = c?.kycReviewStatus === "APPROVED";
  const leads = c?.leads || [];
  const activeLead = leads.find((l) => OPEN_LEAD_STATUSES.has(l.status)) || null;
  const inquiries = c?.inquiries || [];
  const activeInquiries = inquiries.filter((i) => !TERMINAL_INQUIRY_STATUSES.has(i.status));
  const activeInquiry = activeInquiries.length
    ? [...activeInquiries].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0]
    : null;
  const requirementComplete = !!activeInquiry?.completeness?.blockingComplete;
  // "Interested" (item 1) is a Sales-internal flag only — it never gates or
  // reorders this progression (nothing downstream depends on it, matching
  // the backend, which has no such dependency either). Surfaced here purely
  // as an informational marker on the banner/checklist.
  const interested = !!activeInquiry?.customerInterested;
  const quotationSent = !!activeInquiry?.quotationSentAt;
  const acceptanceStatus = activeInquiry?.acceptanceStatus || null;
  const accepted = ["DETECTED", "PENDING_VERIFICATION", "VERIFIED"].includes(acceptanceStatus);
  const alreadySent = acceptanceStatus === "VERIFIED";
  // Item 3/4/5 — mandatory Trade Documents must be complete before the
  // manual Send to Finance handoff is offered, same as KYC/requirement/
  // accepted-quotation below. Defaults to "complete" only when there is no
  // active inquiry to check yet (nothing to block on).
  const tradeDocsMissing = activeInquiry?.tradeDocsMissing || [];
  const tradeDocsComplete = activeInquiry ? !!activeInquiry.tradeDocsComplete : true;
  const readyForHandoff = ["DETECTED", "PENDING_VERIFICATION"].includes(acceptanceStatus) && tradeDocsComplete;

  const checklist = [
    { key: "profile", label: "Customer Profile", done: true },
    { key: "kyc", label: "KYC Approved", done: kycApproved },
    { key: "enquiry", label: "Active Enquiry", done: !!activeInquiry },
    { key: "requirement", label: "Customer Requirement", done: requirementComplete },
    { key: "tradeDocs", label: tradeDocsMissing.length ? `Trade Documents — ${tradeDocsMissing.length} mandatory document${tradeDocsMissing.length !== 1 ? "s" : ""} pending` : "Trade Documents", done: tradeDocsComplete },
    { key: "quotation", label: "Accepted Quotation", done: accepted },
  ];

  // Item 6 — the fuller state progression: Interested -> Requirement
  // Collected -> Quotation Sent -> Customer Accepted -> ready for manual
  // Send to Finance. Never auto-advances into a Finance handoff on its own —
  // the final step always stops at "ready", requiring the explicit
  // Send to Finance action (see the header button / HandoffConfirmDialog).
  let stage, bannerTitle, nextActionText, actionLabel;
  if (!kycApproved) {
    stage = "NEW_CUSTOMER"; bannerTitle = "NEW CUSTOMER";
    nextActionText = "Complete KYC Review"; actionLabel = "Review KYC";
  } else if (!activeInquiry) {
    stage = "KYC_APPROVED"; bannerTitle = "KYC APPROVED";
    nextActionText = "Collect Customer Requirement"; actionLabel = "Add Requirement";
  } else if (!requirementComplete) {
    stage = "REQUIREMENT_IN_PROGRESS"; bannerTitle = "KYC APPROVED";
    nextActionText = "Complete Customer Requirement"; actionLabel = "Complete Requirement";
  } else if (!quotationSent) {
    stage = "REQUIREMENT_COLLECTED"; bannerTitle = interested ? "REQUIREMENT COLLECTED · INTERESTED" : "REQUIREMENT COLLECTED";
    nextActionText = "Request Supplier Rates and Send Quotation"; actionLabel = "Open Enquiry";
  } else if (!accepted) {
    stage = "QUOTATION_SENT"; bannerTitle = "QUOTATION SENT";
    nextActionText = "Awaiting customer acceptance"; actionLabel = "Open Enquiry";
  } else if (!tradeDocsComplete) {
    stage = "QUOTATION_ACCEPTED"; bannerTitle = "CUSTOMER ACCEPTED QUOTATION";
    nextActionText = `${tradeDocsMissing.length} mandatory document${tradeDocsMissing.length !== 1 ? "s" : ""} pending before Finance handoff`; actionLabel = "Open Enquiry";
  } else if (readyForHandoff) {
    stage = "QUOTATION_ACCEPTED"; bannerTitle = "CUSTOMER ACCEPTED QUOTATION";
    nextActionText = "Send to Finance for Review"; actionLabel = "Send to Finance";
  } else {
    stage = "SENT_TO_FINANCE"; bannerTitle = "SENT TO FINANCE";
    nextActionText = "Awaiting Finance review"; actionLabel = null;
  }

  return { kycApproved, leads, activeLead, inquiries, activeInquiry, requirementComplete,
    interested, quotationSent, acceptanceStatus, accepted, tradeDocsMissing, tradeDocsComplete,
    readyForHandoff, alreadySent, checklist,
    stage, bannerTitle, nextActionText, actionLabel };
}

// Aug 2026 IA correction — an Inquiry is never its own page. This IS the
// client-level page: it shows the client header/context, the chronological
// list of this client's inquiries, and — when one is selected — the full
// inquiry detail (everything Workspace.jsx renders) inline, right below
// the list, without ever navigating away from this customer's page. The
// top-right of the header carries the one persistently-visible handoff
// action relevant to whoever is looking (Sales: hand off to Finance once
// acceptance is verified) so it's never buried in scroll.
export default function Customer360() {
  const { id } = useParams();
  const navigate = useNavigate();
  const role = useRole();
  const qc = useQueryClient();
  const { openComposer } = useApp();
  const { data: c } = useCustomer(id);
  const { data: comm } = useCommunications(id);
  const { data: docsData } = useCustomerDocuments(id);
  const { data: me } = useMe();
  // Point 2/3 — Sales-facing KYC doc checklist. Only fetched for Sales
  // (Finance already has its own equivalent panel via FinanceCustomerContext,
  // which fetches this same endpoint independently — no shared cache key
  // conflict since react-query keys on the URL either way).
  const { data: kycChecklistData } = useCustomerDocumentChecklist(role === "sales" ? id : null);
  const [summarizing, setSummarizing] = useState(false);
  const [upload, setUpload] = useState(false);
  const [activeDoc, setActiveDoc] = useState(null);
  const [docOpen, setDocOpen] = useState(false);
  const [activeTrade, setActiveTrade] = useState(null);
  const [tradeOpen, setTradeOpen] = useState(false);
  const [kycBusy, setKycBusy] = useState(false);
  const [kycUploadTarget, setKycUploadTarget] = useState(null);
  const [requirementBusy, setRequirementBusy] = useState(false);
  const [financeChecklistOpen, setFinanceChecklistOpen] = useState(false);

  // Lets Sales upload directly against a specific KYC checklist row (e.g.
  // Aadhaar Card) instead of only being able to "Request Document" from the
  // customer — same category/target convention the original onboarding KYC
  // upload already uses, so it flips that row from Missing to Received.
  // Declared here (before the loading early-return below) since it calls a
  // hook — useFileSelectConfirm must run unconditionally on every render.
  const onKycUpload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("jobId", "auto");
    fd.append("docType", kycUploadTarget || "Document");
    fd.append("customerId", id);
    fd.append("category", "GENERAL");
    try {
      const { data } = await api.post("/documents/upload", fd);
      await qc.invalidateQueries({ queryKey: ["cust-doc-checklist", id] });
      await qc.invalidateQueries({ queryKey: ["cust-docs", id] });
      toast.success(data.aiRead ? "Document uploaded and analyzed — human review required" : "Document uploaded — manual review required");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Upload failed"));
      throw e;
    } finally { setKycUploadTarget(null); }
  };
  const kycUpload = useFileSelectConfirm(onKycUpload);

  // Inline inquiry selection — deep-linkable via ?inquiry=, never a route
  // change. `handoffState` is reported upward by the embedded Workspace
  // panel from the real backend acceptance data (never computed here).
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedInquiryId, setSelectedInquiryId] = useState(searchParams.get("inquiry") || null);
  const [handoffState, setHandoffState] = useState(null);
  const [handoffConfirmOpen, setHandoffConfirmOpen] = useState(false);

  useEffect(() => {
    setSelectedInquiryId(searchParams.get("inquiry") || null);
    setHandoffState(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const selectInquiry = (inquiryId) => {
    const next = new URLSearchParams(searchParams);
    if (inquiryId) next.set("inquiry", inquiryId); else next.delete("inquiry");
    setSearchParams(next, { replace: true });
    setSelectedInquiryId(inquiryId);
    if (inquiryId !== selectedInquiryId) setHandoffState(null);
  };

  if (!c || !comm) return <Loading />;

  const contacts = c.contacts || [];
  const parties = contacts.map((ct) => `${ct.name} · ${ct.role}`);
  const comms = (ch, attachments, intent) => openComposer({ channel: ch, parties, defaultParty: parties[0], customerId: id, intent: intent || `respond to ${c.name} and move the shipment forward`, subjectHint: `[${c.name}] `, attachments });
  const canApprove = me?.caps?.approve;
  const approvals = c.approvals || [];
  const workflow = computeWorkflowState(c);
  const kycRows = (kycChecklistData?.checklist || []).filter((r) => r.kind === "KYC");
  const kycMissing = kycRows.filter((r) => !r.submitted);

  // Point 3 — real, persisted approval (POST /customers/{id}/kyc/approve),
  // gated server-side on every required KYC doc row being Received. Never a
  // visual-only flag.
  const approveKyc = async () => {
    setKycBusy(true);
    try {
      await api.post(`/customers/${id}/kyc/approve`, { expectedVersion: c.version });
      await qc.invalidateQueries({ queryKey: ["customer", id] });
      toast.success("KYC approved");
    } catch (e) { toast.error(apiErrorMessage(e, "Could not approve KYC")); }
    finally { setKycBusy(false); }
  };
  const requestKycDocuments = () => {
    const names = kycMissing.map((r) => r.name).join(", ") || "the outstanding KYC documents";
    comms("email", [], `request the missing KYC document(s) — ${names} — from ${c.name}`);
  };

  // Points 6/7/12 — routes into the EXISTING lead-conversion requirement
  // form (LeadWorkspace.jsx, unchanged) rather than rebuilding a second
  // requirement-collection system. If this customer's lead was already
  // converted (a previous trade completed and its lead is gone), a new Lead
  // is created via the same existing POST /leads endpoint the Pipeline "New
  // lead" dialog already uses — never a second customer/lead record, and
  // never overwriting the completed prior trade (see previousTrades below).
  const addRequirement = async () => {
    if (workflow.activeLead) { navigate(`/sales/leads/${workflow.activeLead.id}`); return; }
    if (workflow.activeInquiry) { selectInquiry(workflow.activeInquiry.id); return; }
    setRequirementBusy(true);
    try {
      const primary = contacts.find((ct) => ct.primary) || contacts[0] || {};
      const { data: lead } = await api.post("/leads", {
        clientId: id, company: c.name, contactName: primary.name,
        contactEmail: primary.email, contactPhone: primary.phone,
        source: "Repeat Business",
      });
      navigate(`/sales/leads/${lead.id}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Could not start a new requirement")); }
    finally { setRequirementBusy(false); }
  };
  const openActiveEnquiry = () => { if (workflow.activeInquiry) selectInquiry(workflow.activeInquiry.id); };
  const goToSendToFinance = () => {
    if (workflow.activeInquiry) { selectInquiry(workflow.activeInquiry.id); toast.info("Review the accepted quotation, then confirm and send to Finance."); }
  };
  const runNextAction = () => {
    if (workflow.stage === "NEW_CUSTOMER") { document.getElementById("c360-kyc-section")?.scrollIntoView({ behavior: "smooth", block: "start" }); return; }
    if (workflow.stage === "KYC_APPROVED") { addRequirement(); return; }
    if (workflow.stage === "REQUIREMENT_IN_PROGRESS") { openActiveEnquiry(); return; }
    if (workflow.stage === "REQUIREMENT_COLLECTED") { openActiveEnquiry(); return; }
    if (workflow.stage === "QUOTATION_SENT") { openActiveEnquiry(); return; }
    if (workflow.stage === "QUOTATION_ACCEPTED") { goToSendToFinance(); return; }
  };
  const documents = docsData?.documents || [];
  const attachments = docsData?.attachments || [];
  const backPath = role === "sales" ? "/sales/customers" : `/${role}/inbox`;
  const jobs = c.jobs || [];
  const previousTrades = jobs.filter((j) => CLOSED_JOB_STATUSES.has(String(j.status || "").toUpperCase()));
  // Bug fix (Aug 2026): the header badge used to always show the CLIENT's
  // aggregate business stage (this client's single most-advanced inquiry or
  // job ever, computed server-side) — for a repeat customer that stage can
  // sit far ahead of whichever inquiry Sales actually opened (e.g. showing
  // "Invoice to Client" while looking at a brand-new DRAFT inquiry still
  // missing requirements), which read as a wrong/inconsistent status. When a
  // specific inquiry is open, show that inquiry's own stage instead — the
  // exact same per-inquiry calculation the Pipeline board's cards already
  // use, so the badge here now matches what was clicked from Pipeline/My
  // Day/Work Queue. The client-level aggregate (sidebar progress card) is
  // untouched — it intentionally reflects overall relationship health, not
  // one inquiry.
  const selectedInquiryRecord = selectedInquiryId ? (c.inquiries || []).find((i) => i.id === selectedInquiryId) : null;
  const headerStage = selectedInquiryRecord ? stageForCommercial(selectedInquiryRecord) : c.businessStage;

  const summarize = async () => {
    setSummarizing(true);
    try {
      await api.post("/ai/summarize", { customerId: id });
      qc.invalidateQueries({ queryKey: ["comms", id] });
      toast.success("Conversation summary generated");
    } catch (e) { toast.error("AI summary unavailable"); }
    finally { setSummarizing(false); }
  };

  const decide = async (aid, decision) => {
    try {
      await api.post(`/approvals/${aid}/decision`, { decision });
      qc.invalidateQueries({ queryKey: ["customer", id] });
      toast.success(decision);
    } catch (e) { toast.error("Not permitted"); }
  };

  const openDoc = (d) => { setActiveDoc(d); setDocOpen(true); };

  return (
    <>
      <PageHeader
        title={<span className="flex items-center gap-2"><button onClick={() => navigate(backPath)} data-testid="c360-back" className="text-slate-400 hover:text-slate-700"><ArrowLeft className="h-4 w-4" /></button>{c.name}</span>}
        subtitle={<div className="flex flex-wrap items-center gap-x-3 gap-y-1"><span>{`${c.id} · ${c.industry} · ${c.location} · Owner ${c.salesOwner}`}</span><BusinessStage stage={headerStage} compact /></div>}
        actions={<div className="flex items-center gap-1.5">
          <Button size="sm" variant="outline" className="h-8" onClick={() => comms("call")} data-testid="c360-call"><Phone className="h-3.5 w-3.5 mr-1.5" />Call</Button>
          <Button size="sm" variant="outline" className="h-8" onClick={() => comms("whatsapp")} data-testid="c360-whatsapp"><MessageCircle className="h-3.5 w-3.5 mr-1.5" />WhatsApp</Button>
          <Button size="sm" variant="outline" className="h-8" onClick={() => comms("email")} data-testid="c360-email"><Mail className="h-3.5 w-3.5 mr-1.5" />Email</Button>
          <Button size="sm" className="h-8 bg-indigo-600 hover:bg-indigo-700" onClick={() => setUpload(true)} data-testid="c360-upload"><UploadCloud className="h-3.5 w-3.5 mr-1.5" />Upload document</Button>
          {/* Aug 2026 IA correction — item 3: the one Sales handoff action for
              this client, always visible top-right, never requiring a scroll
              into the inquiry detail to find it. Eligibility comes straight
              from the embedded Workspace panel's real backend acceptance
              state — never computed here.
              Item 9 (Aug 2026 scope pass) — label changed to "Send to
              Finance for Review" per the product owner; the underlying
              action/eligibility logic (handoffState) is untouched.
              Latest fix — this used to disappear entirely whenever no
              inquiry was selected (or the client had none yet), leaving no
              transfer action visible at all. It now always renders: with an
              inquiry open and eligible it's the real handoff; with an
              inquiry open but not yet eligible, or nothing selected/no
              inquiry exists yet, it opens the point-10 structured checklist
              dialog below — a specific "here's what's missing, here's what
              to do next" instead of a dead-end generic message. */}
          {role === "sales" && (
            selectedInquiryId && handoffState?.eligible ? (
              <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 ml-1" onClick={() => setHandoffConfirmOpen(true)} data-testid="c360-handoff-finance">
                <ArrowRight className="h-3.5 w-3.5 mr-1.5" />Send to Finance for Review
              </Button>
            ) : selectedInquiryId && handoffState?.alreadyDone ? (
              <Button size="sm" variant="outline" className="h-8 ml-1 text-slate-500" disabled
                title="Handed off to Finance" data-testid="c360-handoff-unavailable">
                <ArrowRight className="h-3.5 w-3.5 mr-1.5" />Handed off to Finance
              </Button>
            ) : (
              <Button size="sm" variant="outline" className="h-8 ml-1"
                onClick={() => setFinanceChecklistOpen(true)}
                data-testid="c360-handoff-select-inquiry">
                <ArrowRight className="h-3.5 w-3.5 mr-1.5" />Send to Finance for Review
              </Button>
            )
          )}
        </div>} />

      <div className="flex-1 overflow-y-auto">
        {/* Point 11 — top-of-profile, business-readable "where is this
            customer, what's next" banner. Same computeWorkflowState() the
            Send to Finance checklist dialog uses, so the two can never
            disagree about what's outstanding. */}
        {role === "sales" && (
          <div className={`mx-4 mt-4 md:mx-5 rounded-xl border p-3 flex flex-wrap items-center justify-between gap-2 ${
            workflow.stage === "SENT_TO_FINANCE" ? "border-emerald-200 bg-emerald-50" :
            workflow.stage === "QUOTATION_ACCEPTED" ? "border-emerald-200 bg-emerald-50" :
            "border-indigo-200 bg-indigo-50"}`} data-testid="c360-next-action-banner">
            <div className="text-[13px]">
              <span className="font-semibold text-slate-800">{workflow.bannerTitle}</span>
              <span className="text-slate-500"> — Next Action: {workflow.nextActionText}</span>
            </div>
            {workflow.actionLabel && (
              <Button size="sm" className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700" onClick={runNextAction}
                disabled={kycBusy || requirementBusy} data-testid="c360-next-action-btn">
                {(kycBusy || requirementBusy) ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                {workflow.actionLabel}
              </Button>
            )}
          </div>
        )}
        <div className="grid grid-cols-1 items-start gap-4 p-4 md:p-5 lg:grid-cols-[minmax(240px,280px)_minmax(0,1fr)] xl:grid-cols-[minmax(260px,320px)_minmax(0,1fr)]">
          <div className="min-w-0 space-y-4">
            <BusinessStageProgress stage={c.businessStage} />
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-11 w-11 rounded-xl bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center">{initials(c.name)}</div>
                <div><div className="flex gap-1.5 flex-wrap">{(c.tags || []).map((t) => <span key={t} className="text-[10px] font-medium text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">{t}</span>)}</div><div className="text-[11px] text-slate-400 mt-1">Customer since {c.since}</div></div>
              </div>
              <div className="grid grid-cols-2 gap-y-3 gap-x-2 text-sm pt-2 border-t border-slate-100">
                <Field label="Finance status" value={c.financeStatus} />
                <Field label="Payment terms" value={c.paymentTerms} />
                <Field label="Credit limit" value={c.creditLimit ? money(c.creditLimit) : "Cash"} />
                <Field label="Exposure" value={money(c.exposure)} />
                <Field label="Overdue" value={c.overdue > 0 ? money(c.overdue) : "None"} danger={c.overdue > 0} />
                <Field label="Lifetime revenue" value={money(c.lifetimeRevenue)} />
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Contacts</div>
              <div className="space-y-2.5">
                {contacts.length === 0 && <div className="text-[12.5px] text-slate-400">No contacts on file yet.</div>}
                {contacts.map((ct) => (
                  <div key={ct.email} className="flex items-center gap-2.5">
                    <div className="h-8 w-8 rounded-full bg-slate-100 text-slate-500 text-[11px] font-semibold flex items-center justify-center">{initials(ct.name)}</div>
                    <div className="min-w-0 flex-1"><div className="text-sm font-medium text-slate-800 truncate">{ct.name} {ct.primary && <span className="text-[10px] text-indigo-600">· Primary</span>}</div><div className="text-[11px] text-slate-400 truncate">{[ct.role, ct.phone || ct.whatsapp || ct.email].filter(Boolean).join(" · ") || "—"}</div></div>
                  </div>
                ))}
              </div>
            </div>

            {c.kyc?.expiring && <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"><span className="font-semibold">KYC watch: </span>{c.kyc.expiring}</div>}

            {/* Points 2/3/4 — KYC document review lives on the Sales Customer
                Profile itself (not a separate screen), as its own status
                SEPARATE from the pipeline/business stage above — this
                customer stays visible in Pipeline -> Leads whether KYC is
                pending or approved. Reuses the same document-checklist data
                Finance's equivalent panel (FinanceCustomerContext) already
                fetches; approval is real and persisted via
                POST /customers/{id}/kyc/approve, gated on every KYC doc
                actually being Received. */}
            {role === "sales" && (
              <div id="c360-kyc-section" className="rounded-xl border border-slate-200 bg-white p-4" data-testid="c360-kyc-section">
                <div className="flex items-center justify-between mb-2.5">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">KYC review</div>
                  <StatusPill status={workflow.kycApproved ? "Approved" : "Pending Sales Review"} />
                </div>
                {workflow.kycApproved && (
                  <div className="text-[11px] text-slate-400 mb-2">Approved by {c.kycApprovedBy} · {(c.kycApprovedAt || "").slice(0, 16).replace("T", " ")}</div>
                )}
                <input ref={kycUpload.inputRef} type="file" className="hidden" onChange={kycUpload.onFileChosen} data-testid="c360-kyc-file-input" />
                <div className="grid grid-cols-1 gap-1.5 mb-3">
                  {kycRows.map((r) => {
                    const matchedDoc = r.documentId ? documents.find((d) => d.id === r.documentId) : null;
                    return (
                      <div key={r.name} className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12px] ${r.submitted ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`} data-testid={`c360-kyc-row-${r.name.replace(/\s+/g, "-")}`}>
                        {r.submitted ? <ShieldCheck className="h-3.5 w-3.5 shrink-0" /> : <ShieldAlert className="h-3.5 w-3.5 shrink-0" />}
                        <span className="truncate flex-1">{r.name}</span>
                        <span className="text-[10px] font-semibold uppercase shrink-0">{r.status}</span>
                        {matchedDoc && (
                          <button type="button" onClick={() => openDoc(matchedDoc)} title="Preview" data-testid={`c360-kyc-preview-${r.name.replace(/\s+/g, "-")}`}
                            className="shrink-0 rounded p-0.5 hover:bg-white/70 text-current">
                            <Eye className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button type="button" onClick={() => { setKycUploadTarget(r.name); kycUpload.openPicker(); }} title="Upload" data-testid={`c360-kyc-upload-${r.name.replace(/\s+/g, "-")}`}
                          className="shrink-0 rounded p-0.5 hover:bg-white/70 text-current">
                          <UploadCloud className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    );
                  })}
                  {kycRows.length === 0 && <div className="text-[12px] text-slate-400">Loading checklist…</div>}
                </div>
                {kycUpload.pending && (
                  <div className="mb-3">
                    <div className="text-[11px] text-slate-500 mb-1">Uploading for: {kycUploadTarget}</div>
                    <FileSelectConfirmBar pending={kycUpload.pending} busy={kycUpload.busy} onSubmit={kycUpload.submit}
                      onCancel={kycUpload.cancel} onReUpload={kycUpload.reUpload} submitLabel="Upload" />
                  </div>
                )}
                {!workflow.kycApproved && (
                  <div className="flex gap-2">
                    <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" disabled={kycBusy || kycMissing.length > 0}
                      onClick={approveKyc} data-testid="c360-kyc-approve"
                      title={kycMissing.length > 0 ? `Missing: ${kycMissing.map((r) => r.name).join(", ")}` : undefined}>
                      {kycBusy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}Approve KYC
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={requestKycDocuments} data-testid="c360-kyc-request-doc">
                      Request Document
                    </Button>
                  </div>
                )}
              </div>
            )}

            <div className="empty:hidden rounded-xl border border-slate-200 bg-white p-4 space-y-3">
              <RelatedGroup title="Quotations" icon={FileText} items={(c.quotations || []).map((q) => ({ id: q.id, label: `${money(q.sellTotal ?? q.total)} · ${q.version}`, status: q.status, onClick: role === "sales" ? () => (q.inquiryId ? selectInquiry(q.inquiryId) : navigate(`/sales/quotations/${q.id}`)) : null }))} />
              {/* Item 6 — Job Details must never be shown in the Sales
                  Dashboard: the "Jobs" group (which links into Operations'
                  JobDetail page) is hidden for Sales. Unchanged for every
                  other role. Sales instead gets "Previous Trades" below
                  (item 7) — a commercial summary, never Job Details. */}
              {role !== "sales" && jobs.length > 0 && <RelatedGroup title="Jobs" icon={Boxes} items={jobs.map((j) => ({ id: j.id, label: `${j.mode} · ${j.lane}`, status: j.status, onClick: () => navigate(`/${role}/jobs/${j.id}`) }))} />}
            </div>

            {/* Item 7 — "Previous Trades": each past shipment as a separate,
                clickable, Sales-safe commercial-summary record (route,
                mode, value, date, status) — opens a modal, never
                Operations' Job Detail page. */}
            {role === "sales" && previousTrades.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="c360-previous-trades">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5"><History className="h-3 w-3" /> Previous Trades</div>
                <div className="space-y-1">
                  {previousTrades.map((t) => (
                    <button key={t.id} onClick={() => { setActiveTrade(t); setTradeOpen(true); }} data-testid={`trade-${t.id}`}
                      className="w-full flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-slate-50 transition-colors">
                      <span className="min-w-0"><span className="text-[11px] font-mono-num text-slate-400 block">{t.id}</span><span className="text-sm text-slate-700 truncate block">{t.mode || "—"} · {t.lane || "—"}</span></span>
                      <span className="flex items-center gap-1.5 shrink-0"><StatusPill status={t.status} /><ChevronRight className="h-3.5 w-3.5 text-slate-300" /></span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="min-w-0 space-y-4">
            <AICard title="Customer Conversation Summary" footer={
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-xs font-medium text-violet-700">{comm.intent ? `Detected intent: ${comm.intent}` : "Generate a live summary of this history"}</span>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => comms("whatsapp")} data-testid="c360-ai-reply">Draft reply</Button>
              </div>
            }>
              {comm.aiSummary ? comm.aiSummary : (
                <button onClick={summarize} disabled={summarizing} data-testid="c360-summarize" className="inline-flex items-center gap-1.5 text-sm font-medium text-violet-700 hover:underline disabled:opacity-60">
                  {summarizing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  {summarizing ? "Summarizing conversation…" : "Summarize this conversation with AI"}
                </button>
              )}
            </AICard>

            {role === "sales" && (workflow.activeInquiry || workflow.activeLead) && (
              <RequirementsSection
                source={workflow.activeInquiry || workflow.activeLead}
                sourceType={workflow.activeInquiry ? "inquiry" : "lead"}
                customerId={id}
                onSaved={() => qc.invalidateQueries({ queryKey: ["customer", id] })}
              />
            )}

            {/* Aug 2026 IA correction — item 3: this IS the client-level
                page. Every inquiry for this client, sorted by date
                received, is listed here; selecting one expands its full
                detail INLINE below the list (never a route change to a
                separate inquiry page) — an agent handling many leads a
                day always keeps this client's context above the detail
                they're looking at. */}
            {(c.inquiries || []).length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="c360-all-inquiries">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2.5">All inquiries · {c.inquiries.length} · sorted by date received</div>
                <div className="divide-y divide-slate-100">
                  {[...c.inquiries]
                    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
                    .map((i) => {
                      const isSelected = i.id === selectedInquiryId;
                      return (
                        <button
                          key={i.id}
                          onClick={() => role === "sales" && selectInquiry(isSelected ? null : i.id)}
                          data-testid={`c360-inquiry-${i.id}`}
                          aria-expanded={isSelected}
                          className={`w-full text-left py-2.5 flex items-center gap-2 transition-colors rounded-lg px-1 ${isSelected ? "bg-indigo-50/70" : "hover:bg-slate-50"}`}
                        >
                          <Boxes className={`h-4 w-4 shrink-0 ${isSelected ? "text-indigo-600" : "text-indigo-500"}`} />
                          <div className="min-w-0 flex-1">
                            <div className={`text-sm font-medium truncate ${isSelected ? "text-indigo-900" : "text-slate-800"}`}>{i.mode || ""} · {i.lane || ""}</div>
                            <div className="text-[11px] text-slate-400 font-mono-num">{i.id} · Received {(i.createdAt || "").slice(0, 10) || "—"}</div>
                          </div>
                          <StatusPill status={i.status} />
                          {role === "sales" && (isSelected ? <ChevronDown className="h-3.5 w-3.5 text-indigo-500" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-300" />)}
                        </button>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Inline inquiry detail — the full former Workspace.jsx content,
                rendered in place. Closing it returns to the plain client
                page; it is never a navigation. */}
            {role === "sales" && selectedInquiryId && (
              <div className="rounded-xl border border-indigo-200 bg-white p-3 sm:p-4" data-testid="c360-inquiry-detail">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-indigo-500">Inquiry detail</div>
                  <button onClick={() => selectInquiry(null)} data-testid="c360-inquiry-detail-close" className="text-[11px] font-medium text-slate-400 hover:text-slate-700">
                    Collapse
                  </button>
                </div>
                <Workspace embedded inquiryId={selectedInquiryId} onSelectSibling={selectInquiry} onHandoffState={setHandoffState} />
              </div>
            )}

            {approvals.length > 0 && (
              <div className="rounded-xl border border-amber-200 bg-white p-4" data-testid="c360-approvals">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-600 mb-2.5">Approvals for this lead · {approvals.length}</div>
                <div className="space-y-2.5">
                  {approvals.map((a) => {
                    const decided = a.status !== "Pending";
                    return (
                      <div key={a.id} data-testid={`c360-approval-${a.id}`} className={`rounded-lg border p-3 ${decided ? "border-slate-200 opacity-70" : "border-amber-200 bg-amber-50/40"}`}>
                        <div className="flex items-center gap-2 mb-1"><span className="text-sm font-semibold text-slate-800">{a.type}</span><StatusPill status={a.status === "Pending" ? "Approval Pending" : a.status} /></div>
                        <div className="text-[12px] text-slate-600">{a.context}</div>
                        <div className="mt-1.5 text-[12px] text-violet-800 flex items-start gap-1.5"><AIBadge>AI</AIBadge><span className="flex-1">{a.ai}</span></div>
                        {!decided && (
                          <div className="flex items-center justify-end gap-2 mt-2">
                            {canApprove ? (
                              <>
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => decide(a.id, "Rejected")} data-testid={`c360-reject-${a.id}`}><X className="h-3 w-3 mr-1" />Reject</Button>
                                <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => decide(a.id, "Approved")} data-testid={`c360-approve-${a.id}`}><Check className="h-3 w-3 mr-1" />Approve</Button>
                              </>
                            ) : <span className="text-[11px] text-slate-400">Awaiting {a.role} approval</span>}
                          </div>
                        )}
                        {decided && a.decidedBy && <div className="mt-1.5 text-[11px] text-slate-400">Decided by {a.decidedBy}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Aug 2026 — Finance had no "what should I do next" surface at
                all on the Customer Profile, unlike Sales (next-action
                banner + panel) and Operations (Next action card on Job
                Detail). Mirrors their exact style: grounded, deterministic
                base (GET /customers/{id}/finance-next-action) with an
                optional AI-composed narrative layered on top, same
                aiGenerated fallback contract. */}
            {role === "finance" && <FinanceNextActionCard customerId={id} />}

            {/* Aug 2026 Finance scope pass (round 3) — Finance gets the
                4-section (KYC / Trade / Operations / Quotation) documents
                view instead of the flat list every other role keeps
                unchanged below. Same underlying `documents`/`quotations`
                data, no duplicate fetch. */}
            {role === "finance" ? (
              <FinanceDocumentsSections documents={documents} quotations={c.quotations || []} onOpenDoc={openDoc} />
            ) : (
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="c360-documents">
              <div className="flex items-center justify-between mb-2.5">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Documents · {documents.length + attachments.length}</div>
                <button onClick={() => setUpload(true)} data-testid="c360-doc-upload" className="text-[11px] font-medium text-indigo-600 hover:underline inline-flex items-center gap-1"><UploadCloud className="h-3 w-3" />Upload</button>
              </div>
              {documents.length + attachments.length === 0 ? (
                <div className="text-[13px] text-slate-400 py-2">No documents yet. Upload a scan or PDF for routing and human review.</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {documents.map((d) => (
                    <button key={d.id} onClick={() => openDoc(d)} data-testid={`c360-doc-${d.id}`} className="w-full text-left py-2.5 flex items-center gap-3 hover:bg-slate-50 transition-colors rounded-lg px-1">
                      <FileText className="h-4 w-4 text-indigo-500 shrink-0" />
                      <div className="min-w-0 flex-1"><div className="text-sm font-medium text-slate-800 truncate">{d.name}</div><div className="text-[11px] text-slate-400">{d.type} · {d.source} · {d.date}</div></div>
                      <StatusPill status={d.review} />
                      <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
                    </button>
                  ))}
                  {attachments.map((a) => (
                    <div key={a.id} data-testid={`c360-att-${a.id}`} className="w-full py-2.5 flex items-center gap-3 px-1">
                      <Paperclip className="h-4 w-4 text-slate-400 shrink-0" />
                      <div className="min-w-0 flex-1"><div className="text-sm font-medium text-slate-700 truncate">{a.name}</div><div className="text-[11px] text-slate-400">{a.dir === "in" ? "Received" : "Shared"} · {a.source} · {a.date}</div></div>
                      <span className="text-[10px] uppercase tracking-wider text-slate-400">{a.channel}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}

            {/* Aug 2026 Finance scope pass (round 3) — Final Customer
                Invoice, Costs & Variance, Payment & Outstanding. Finance
                only; reuses c.invoices/c.jobs/c.approvals already fetched
                above (no duplicate customer fetch) and the same real
                backend endpoints Receivables.jsx/Payables.jsx/Pipeline.jsx
                already use. */}
            {role === "finance" && (
              <FinanceFinalInvoice customerId={id} invoices={c.invoices || []} documents={documents} onOpenDoc={openDoc} onUpload={() => setUpload(true)} comms={comms} />
            )}
            {role === "finance" && (
              <FinanceCostsVariance jobs={jobs} approvals={approvals} documents={documents} onOpenDoc={openDoc} />
            )}
            {role === "finance" && (
              <FinancePaymentOutstanding invoices={c.invoices || []} comms={comms} customer={c} />
            )}

            <div className="flex flex-wrap items-center justify-between gap-1">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Communication history · Email · WhatsApp · Call</div>
              <span className="text-[11px] text-slate-400">{comm.threads.length} interactions</span>
            </div>
            <CommunicationTimeline threads={[...comm.threads].reverse()} documents={documents} onPreviewDocument={openDoc} />
          </div>
        </div>
      </div>

      <AutoFileDialog open={upload} onOpenChange={(v) => { setUpload(v); if (!v) qc.invalidateQueries({ queryKey: ["cust-docs", id] }); }} customerId={id} />
      <DocumentPanel doc={activeDoc} open={docOpen} onOpenChange={setDocOpen} />
      <TradeDetailDialog trade={activeTrade} open={tradeOpen} onOpenChange={setTradeOpen} />
      {role === "sales" && handoffState?.eligible && (
        <HandoffConfirmDialog
          open={handoffConfirmOpen}
          onOpenChange={setHandoffConfirmOpen}
          busy={handoffState.busy}
          title={handoffState.title}
          confirmLabel={handoffState.confirmLabel}
          summaryRows={handoffState.summaryRows}
          receivingTeamSees={handoffState.receivingTeamSees}
          onConfirm={async () => { await handoffState.onConfirm(); setHandoffConfirmOpen(false); }}
        />
      )}
      {/* Point 10 — replaces the old generic "no inquiry yet" dead end with
          a specific checklist of what's missing and one concrete next
          action, driven by the same computeWorkflowState() the top banner
          uses. */}
      {role === "sales" && (
        <Dialog open={financeChecklistOpen} onOpenChange={setFinanceChecklistOpen}>
          <DialogContent className="sm:max-w-md" data-testid="c360-finance-checklist-dialog">
            <DialogHeader>
              <DialogTitle className="font-display">Cannot Send to Finance — yet</DialogTitle>
              <DialogDescription>Complete the following first:</DialogDescription>
            </DialogHeader>
            <div className="space-y-1.5">
              {workflow.checklist.map((row) => (
                <div key={row.key} className={`flex items-center gap-2 text-sm ${row.done ? "text-emerald-700" : "text-slate-500"}`} data-testid={`c360-finance-checklist-${row.key}`}>
                  {row.done ? <Check className="h-4 w-4 text-emerald-600 shrink-0" /> : <X className="h-4 w-4 text-slate-300 shrink-0" />}
                  {row.label}
                </div>
              ))}
            </div>
            {workflow.actionLabel && (
              <Button size="sm" className="mt-2 bg-indigo-600 hover:bg-indigo-700"
                onClick={() => { setFinanceChecklistOpen(false); runNextAction(); }}
                data-testid="c360-finance-checklist-action">
                {workflow.actionLabel}
              </Button>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

function Field({ label, value, danger }) {
  return <div><div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div><div className={`text-sm font-medium font-mono-num ${danger ? "text-red-600" : "text-slate-800"}`}>{value}</div></div>;
}

function RelatedGroup({ title, icon: Icon, items }) {
  if (!items.length) return null;
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5"><Icon className="h-3 w-3" /> {title}</div>
      <div className="space-y-1">
        {items.map((it) => {
          const Component = it.onClick ? "button" : "div";
          return (
          <Component key={it.id} onClick={it.onClick || undefined} data-testid={`related-${it.id}`} className={`w-full flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left ${it.onClick ? "hover:bg-slate-50 transition-colors" : "cursor-default"}`}>
            <span className="min-w-0"><span className="text-[11px] font-mono-num text-slate-400 block">{it.id}</span><span className="text-sm text-slate-700 truncate block">{it.label}</span></span>
            <span className="flex items-center gap-1.5 shrink-0"><StatusPill status={it.status} />{it.onClick && <ChevronRight className="h-3.5 w-3.5 text-slate-300" />}</span>
          </Component>
          );
        })}
      </div>
    </div>
  );
}
